import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Minus, Plus, X, ShoppingBag, ArrowRight } from 'lucide-react'
import { useCart } from '../context/CartContext'
import Button from '../components/ui/Button'

export default function Cart() {
  const { cart, cartTotal, updateQuantity, removeFromCart } = useCart()

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
    }).format(price)
  }

  const shipping = cartTotal >= 75 ? 0 : 9.95
  const total = cartTotal + shipping

  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-pp-ivory">
        <div className="container mx-auto px-4 lg:px-8 py-16 lg:py-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-md mx-auto text-center"
          >
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-pp-sand/50 flex items-center justify-center">
              <ShoppingBag className="w-10 h-10 text-pp-stone" />
            </div>
            <h1 className="font-display text-display-md text-pp-ink mb-4">
              Your cart is empty
            </h1>
            <p className="text-pp-stone mb-8">
              Looks like you haven't added any items to your cart yet.
            </p>
            <Button as={Link} to="/shop">
              Start Shopping
              <ArrowRight className="w-5 h-5" />
            </Button>
          </motion.div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-pp-ivory">
      <div className="container mx-auto px-4 lg:px-8 py-8 lg:py-12">
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="font-display text-display-md text-pp-ink mb-8"
        >
          Your Cart
        </motion.h1>

        <div className="grid lg:grid-cols-3 gap-8 lg:gap-12">
          {/* Cart Items */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="lg:col-span-2 space-y-4"
          >
            {cart.map((item) => (
              <div
                key={item.id}
                className="bg-white rounded-2xl p-4 lg:p-6 border border-pp-sand/50 flex gap-4 lg:gap-6"
              >
                {/* Product Image */}
                <div className="w-24 h-24 lg:w-32 lg:h-32 bg-pp-sand/30 rounded-xl flex-shrink-0 flex items-center justify-center">
                  <span className="text-pp-stone text-xs">Image</span>
                </div>

                {/* Product Details */}
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between gap-4">
                    <div>
                      <Link
                        to={`/product/${item.slug}`}
                        className="font-display text-lg text-pp-ink hover:text-pp-terracotta transition-colors"
                      >
                        {item.name}
                      </Link>
                      <p className="text-pp-stone text-sm mt-1 line-clamp-1">
                        {item.shortDescription}
                      </p>
                    </div>
                    <button
                      onClick={() => removeFromCart(item.id)}
                      className="p-1 text-pp-stone hover:text-pp-terracotta transition-colors flex-shrink-0"
                      aria-label="Remove item"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  <div className="flex items-center justify-between mt-4">
                    {/* Quantity */}
                    <div className="flex items-center border border-pp-sand rounded-lg">
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity - 1)}
                        className="p-2 text-pp-charcoal hover:text-pp-terracotta transition-colors"
                        aria-label="Decrease quantity"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <span className="px-3 text-sm font-medium text-pp-ink">
                        {item.quantity}
                      </span>
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        className="p-2 text-pp-charcoal hover:text-pp-terracotta transition-colors"
                        aria-label="Increase quantity"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Price */}
                    <span className="font-medium text-pp-ink">
                      {formatPrice(item.price * item.quantity)}
                    </span>
                  </div>
                </div>
              </div>
            ))}

            <Link
              to="/shop"
              className="inline-flex items-center gap-2 text-pp-stone hover:text-pp-charcoal transition-colors mt-4"
            >
              <ArrowRight className="w-4 h-4 rotate-180" />
              Continue Shopping
            </Link>
          </motion.div>

          {/* Order Summary */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="bg-white rounded-2xl p-6 border border-pp-sand/50 sticky top-24">
              <h2 className="font-display text-xl text-pp-ink mb-6">
                Order Summary
              </h2>

              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-pp-stone">Subtotal</span>
                  <span className="text-pp-charcoal">{formatPrice(cartTotal)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-pp-stone">Shipping</span>
                  <span className="text-pp-charcoal">
                    {shipping === 0 ? 'Free' : formatPrice(shipping)}
                  </span>
                </div>
                {shipping > 0 && (
                  <p className="text-xs text-pp-sage">
                    Add {formatPrice(75 - cartTotal)} more for free shipping
                  </p>
                )}
              </div>

              <div className="border-t border-pp-sand my-4 pt-4">
                <div className="flex justify-between font-medium">
                  <span className="text-pp-ink">Total</span>
                  <span className="text-pp-ink">{formatPrice(total)}</span>
                </div>
                <p className="text-xs text-pp-stone mt-1">
                  Including GST
                </p>
              </div>

              <Button as={Link} to="/checkout" className="w-full mt-4">
                Proceed to Checkout
                <ArrowRight className="w-5 h-5" />
              </Button>

              <p className="text-xs text-pp-stone text-center mt-4">
                Secure checkout powered by Stripe
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  )
}
